import { SearchRequest } from 'api/SearchRequest';
import { LockerSearchRequest } from 'api/searchRequest/LockerSearchRequest';
import { LotoCommunicationSearchRequest } from 'api/searchRequest/LotoCommunicationSearchRequest';
import { LotoLogSearchRequest } from 'api/searchRequest/LotoLogSearchRequest';
import { LotoOrderSearchRequest } from 'api/searchRequest/LotoOrderSearchRequest';
import { LotoPermissionSearchRequest } from 'api/searchRequest/LotoPermissionSearchRequest';
import { LotoSettingSearchRequest } from 'api/searchRequest/LotoSettingSearchRequest';
import { LotoTransferSearchRequest } from 'api/searchRequest/LotoTransferSearchRequest';
import { UserSearchRequest } from 'api/searchRequest/UserSearchRequest';
import { UserSelectSearchRequest } from 'api/searchRequest/UserSelectSearchRequest';

export const dashboardKeys = {
  detail: (factoryId: string) => ['dashboard', factoryId] as const,
};
export const mapDashboardKeys = {
  list: (factoryId: string) => ['maPDashboard', factoryId] as const,
};

export const mapEquipKeys = {
  all: ['maPDashboard'] as const,
};

export const plantImageKeys = {
  all: ['plantImage'] as const,
};

export const lotoConclusionKeys = {
  all: ['lotoConclusion'] as const,
  list: (filters: LotoOrderSearchRequest | LotoTransferSearchRequest) =>
    [
      'lotoConclusion',
      'list',
      { page: filters.page, count: filters.count, localId: filters.localId },
    ] as const,
  details: () => [...lotoConclusionKeys.all, 'detail'] as const,
  detail: (id: number) => [...lotoConclusionKeys.details(), id] as const,
};

export const permissionTransferKeys = {
  all: ['lotoPermissions'] as const,
  list: (filters: LotoPermissionSearchRequest) =>
    [
      'lotoPermissions',
      'list',
      { page: filters.page, count: filters.count, localId: filters.localId },
    ] as const,
};

export const lotoCommunicationKeys = {
  all: ['lotoCommunication'] as const,
  list: (filters: LotoCommunicationSearchRequest) =>
    [
      'lotoCommunication',
      'list',
      { page: filters.page, count: filters.count, localId: filters.localId },
    ] as const,
};

export const lotoActionLogKeys = {
  all: ['lotoActionLog'] as const,
  list: (filters: LockerSearchRequest) =>
    [
      'lotoActionLog',
      'list',
      { page: filters.page, count: filters.count, localId: filters.localId },
    ] as const,
};

export const lotoSettingKeys = {
  all: ['lotoSettings'] as const,
  list: (filters: LotoSettingSearchRequest) =>
    [
      'lotoSettings',
      'list',
      { page: filters.page, count: filters.count, localId: filters.localId },
    ] as const,
  details: () => [...lotoSettingKeys.all, 'detail'] as const,
  detail: (id: number) => [...lotoSettingKeys.details(), id] as const,
};

export const lotoBoxKeys = {
  all: ['lotoBoxes'] as const,
  list: (filters: LotoSettingSearchRequest) =>
    [
      'lotoBoxes',
      'list',
      { page: filters.page, count: filters.count, localId: filters.localId },
    ] as const,
};

export const lotoLogKeys = {
  all: ['lotoLogs'] as const,
  list: (filters: LotoLogSearchRequest) =>
    ['lotoLogs', 'list', { page: filters.page, count: filters.count, localId: filters.localId }] as const,
  details: () => [...lotoLogKeys.all, 'detail'] as const,
  detail: (id: number) => [...lotoLogKeys.details(), id] as const,
};

export const lotoPermissionKeys = {
  all: ['lotoPermissions'] as const,
  list: (filters: LotoPermissionSearchRequest) =>
    [
      'lotoPermissions',
      'list',
      { page: filters.page, count: filters.count, localId: filters.localId },
    ] as const,
};

export const lotoActionKeys = {
  all: ['lotoActions'] as const,
  list: (orderId: number, filters: SearchRequest) => [
    lotoActionKeys.all,
    'list',
    { orderId, page: filters.page, count: filters.count },
  ],
};

export const lockerKeys = {
  all: ['lockers'] as const,
  list: (filters: LockerSearchRequest) =>
    ['lockers', 'list', { page: filters.page, count: filters.count, localId: filters.localId }] as const,
  details: () => [...lockerKeys.all, 'detail'] as const,
  detail: (id: number) => [...lockerKeys.details(), id] as const,
};

export const factoryKeys = {
  all: ['factories'] as const,
  details: () => [...factoryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...factoryKeys.details(), id] as const,
};
export const factoryCodeKeys = {
  all: ['factoryCodes'] as const,
  details: () => [...factoryCodeKeys.all, 'detail'] as const,
  detail: (localId: string) => [...factoryCodeKeys.details(), localId] as const,
};

export const partKeys = {
  all: ['parts'] as const,
  list: (factoryId: string) => ['parts', 'list',] as const,
  details: () => [...partKeys.all, 'detail'] as const,
  detail: (id: number) => [...partKeys.details(), id] as const,
};

export const zoneKeys = {
  all: ['zones'] as const,
  list: (partId: number | string) => ['zones', 'list', { partId }] as const,
  listByFactory: (factoryId: string) =>
    ['zones', 'list', 'factory', { factoryId }] as const,
  details: () => [...zoneKeys.all, 'detail'] as const,
  detail: (id: number) => [...zoneKeys.details(), id] as const,
};

export const sectionKeys = {
  all: ['sections'] as const,
  list: (zoneId: number | string) => ['sections', 'list', { zoneId }] as const,
  listByFactory: (factoryId: string) =>
    ['sections', 'list', 'factory', { factoryId }] as const,
  details: () => [...sectionKeys.all, 'detail'] as const,
  detail: (id: number) => [...sectionKeys.details(), id] as const,
};

export const deviceKeys = {
  all: ['devices'] as const,
  list: (sectionId: number | string, searchKey?: string) => ['devices', 'list', { sectionId, searchKey }] as const,
  details: () => [...deviceKeys.all, 'detail'] as const,
  detail: (id: number) => [...deviceKeys.details(), id] as const,
};
export const equipQrKeys = {
  all: ['equipsQr'] as const,
};

export const adminKeys = {
  all: ['admins'] as const,
  list: (filters: SearchRequest) => ['admins', 'list', { ...filters }] as const,
  details: () => [...adminKeys.all, 'detail'] as const,
  detail: (id: number) => [...adminKeys.details(), id] as const,
};

export const userInfoKeys = {
  all: ['useInfos'] as const,
  list: (filters: UserSearchRequest | UserSelectSearchRequest) =>
    ['useInfos', 'list', { page: filters.page, count: filters.count, local: filters.localId }] as const,
  details: () => [...userInfoKeys.all, 'detail'] as const,
  detail: (id: number) => [...userInfoKeys.details(), id] as const,
  ping: (path: string) => ['userInfo', path]
};

export const userAuthKeys = {
  all: ['userAuths'] as const,
  list: (filters: UserSearchRequest) =>
    [
      'userAuths',
      'list',
      { page: filters.page, count: filters.count, localId: filters.localId },
    ] as const,
  details: () => [...userAuthKeys.all, 'detail'] as const,
  detail: (id: number) => [...userAuthKeys.details(), id] as const,
};

export const shiftWorkKeys = {
  all: ['shiftWorkKeys'] as const,
  list: (filters: SearchRequest) =>
    [
      'shiftWorkKeys',
      'list',
      { page: filters.page, count: filters.count },
    ] as const,
  details: () => [...userAuthKeys.all, 'detail'] as const,
  detail: (id: number) => [...userAuthKeys.details(), id] as const,
}

export const delegateKeys = {
  all: ['delegateKeys'] as const,
  list: (filters: SearchRequest) => ['delegateKeys', 'list',] as const,
  date: (factoryId: string) => ['delegateKeys', factoryId] as const,
  count: (path: string) => ['delegateKeys', 'count', path] as const
}

export const settingKeys = {
  all: ['settingKeys'] as const,
  filter: (factoryId: string) => [settingKeys.all, factoryId]
}

export const localKeys = {
  all: ['localKeys'] as const,
};

export const tagKeys = {
  all: ['tagKeys'] as const,
  list: (filters: LotoSettingSearchRequest) => ['tagKeys', 'list',] as const,
  detail: (tagId: number) => ['tagKeys', 'detail', tagId]
}